import type { refreshTokenApi } from '~/api/auth';

/**
 * auth 流程裡「跨呼叫共享」的那幾個狀態
 *
 * 進行中的 refresh、已經觸發的強制登出、auth 世代編號 —— 這些的意義都是
 * 「同一個使用者的多支請求要互相看得到彼此」，所以不能是各自的區域變數。
 *
 * 【但也不能直接放模組層】瀏覽器端一個分頁一份，模組層剛好等於「這個使用者」；
 * 伺服器端的模組層卻是**整個 Node process 共用**，一台 Vercel lambda 同時服務多少個
 * 使用者就共用給多少人。實際會發生的事：
 *
 *   - `forceLogoutPromise` 刻意不重設（瀏覽器端會整頁重載，狀態自然消失），
 *     在伺服器端就變成「一旦有人被強制登出，這台 lambda 之後的每一個請求
 *     `isForcingLogout()` 都回 true」—— 路由守衛的錯誤分流會全部變成無條件放行、
 *     「登入已過期」的提示也會被永久壓掉
 *   - `authGeneration` 被別人的登入／切組織推進，會讓你這支 refresh 的結果被誤判成
 *     舊世代而丟棄
 *   - 共用進行中的 refresh promise，等於把 A 的換 token 結果回給 B
 *
 * 所以這裡把整組狀態綁在 request 上：瀏覽器端仍是單一份（行為與過去完全相同），
 * 伺服器端則以 Nuxt app instance（每個請求一個）為鍵各自持有。
 *
 * 搭配 `useAuth` / `useApiFetch` 裡的 `import.meta.server` 短路一起看：伺服器端本來就
 * 不該換 token（換到的新 token 送不回瀏覽器，卻已經把一次性的 refresh token 消費掉），
 * 這份隔離是那道防線之外的第二層保險。
 */
export interface AuthRuntimeState {
  /** 進行中的強制登出，併發的 401 共用同一個，確保只跳轉一次 */
  forceLogoutPromise: Promise<void> | null;
  /** 進行中的換 token 請求 */
  tokenRefreshPromise: Promise<Awaited<ReturnType<typeof refreshTokenApi>>> | null;
  /** `tokenRefreshPromise` 是拿哪一張 refresh token 換的，用來判斷能不能共用 */
  tokenRefreshKey: string;
  /** auth state 的世代編號，切組織／登入／登出會推進它讓在途的舊 refresh 作廢 */
  authGeneration: number;
  /** useApiFetch 的 401 自動續期，避免並發的 401 各送一支 */
  apiRefreshTokenPromise: Promise<boolean> | null;
  /** useApiFetch 的 CSRF 403 自動續期 */
  apiRefreshCsrfPromise: Promise<boolean> | null;
}

const createAuthRuntimeState = (): AuthRuntimeState => ({
  forceLogoutPromise: null,
  tokenRefreshPromise: null,
  tokenRefreshKey: '',
  authGeneration: 0,
  apiRefreshTokenPromise: null,
  apiRefreshCsrfPromise: null,
});

/** 瀏覽器端只有一個使用者，模組層就是正確的範圍 */
const clientState = createAuthRuntimeState();

/** 伺服器端以 Nuxt app instance（每個請求一個）為鍵，各請求各自持有 */
const serverStates = new WeakMap<object, AuthRuntimeState>();

/**
 * 取得目前 request 的 auth 共享狀態。
 *
 * 伺服器端拿不到 Nuxt context 時（例如在元件生命週期之外被呼叫）會回一份用完即丟的，
 * 寧可「這一次不共用」也不要退回跨請求共用。
 */
export const useAuthRuntime = (): AuthRuntimeState => {
  if (import.meta.client) {
    return clientState;
  }

  let nuxtApp: object | null = null;
  try {
    nuxtApp = useNuxtApp();
  } catch {
    // 沒有 Nuxt instance：給一份獨立的，不落回共用狀態
    nuxtApp = null;
  }
  if (!nuxtApp) {
    return createAuthRuntimeState();
  }

  let state = serverStates.get(nuxtApp);
  if (!state) {
    state = createAuthRuntimeState();
    serverStates.set(nuxtApp, state);
  }
  return state;
};
